/**
 * AuditLogEditor — configuration panel for workflow audit_log step nodes.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { X, FileText, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { useActionEditorDraft } from '../../hooks/useActionEditorDraft';
import WorkflowStepEditorShell from './WorkflowStepEditorShell';

const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const AuditLogEditor = ({
  isOpen,
  onClose,
  step,
  onSave,
  workflowId,
  token,
  isNewWorkflow = false,
  width,
  overlayMode,
  onOverlayDismiss,
  activePanelType = 'audit-log',
  chromeHeight,
}) => {
  const [label, setLabel] = useState('');
  const [eventName, setEventName] = useState('');
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState('info');
  const [includeContext, setIncludeContext] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!step) return;
    const config = step.config || {};
    setLabel(step.label || step.name || '');
    setEventName(config.event_name || config.event || '');
    setMessage(config.message || config.template || config.note || '');
    setSeverity(config.severity || 'info');
    setIncludeContext(config.include_context !== false);
  }, [step]);

  const draft = useMemo(
    () => ({ label, eventName, message, severity, includeContext }),
    [label, eventName, message, severity, includeContext],
  );
  const mapStepToDraft = useCallback((s) => {
    const config = s?.config || {};
    return {
      label: s?.label || s?.name || '',
      eventName: config.event_name || config.event || '',
      message: config.message || config.template || config.note || '',
      severity: config.severity || 'info',
      includeContext: config.include_context !== false,
    };
  }, []);
  const { guardedClose, commitDraft } = useActionEditorDraft({
    editorId: step ? `audit-log-${step.id}` : 'audit-log',
    isOpen,
    step,
    draft,
    mapStepToDraft,
  });
  const requestClose = useCallback(() => guardedClose(onClose), [guardedClose, onClose]);

  if (!isOpen || !step) return null;

  const handleSave = async () => {
    const updatedStep = {
      ...step,
      type: 'audit_log',
      label: label.trim() || 'Audit Log',
      name: label.trim() || 'Audit Log',
      config: {
        ...(step.config || {}),
        event_name: eventName.trim(),
        message: message.trim(),
        template: message.trim(),
        severity,
        include_context: includeContext,
        configured: true,
      },
    };

    if (!isNewWorkflow && workflowId && token) {
      setSaving(true);
      try {
        const res = await axios.patch(
          `${API}/platform/system-automations/workflows/${workflowId}/nodes/${step.id}`,
          {
            config: updatedStep.config,
            label: updatedStep.label,
            name: updatedStep.name,
            type: 'audit_log',
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        onSave(res.data?.node || updatedStep);
        toast.success('Audit log saved');
        commitDraft();
        onClose();
      } catch (err) {
        const detail = err.response?.data?.detail;
        const messageText = Array.isArray(detail)
          ? detail.map((item) => item.msg || item).join(', ')
          : detail || 'Failed to save audit log';
        toast.error(messageText);
      } finally {
        setSaving(false);
      }
      return;
    }

    onSave(updatedStep);
    toast.success('Audit log saved');
    commitDraft();
    onClose();
  };

  return (
    <WorkflowStepEditorShell
      isOpen={isOpen}
      width={width}
      overlayMode={overlayMode}
      onOverlayDismiss={onOverlayDismiss}
      activePanelType={activePanelType}
      chromeHeight={chromeHeight}
      className="bg-zinc-900 border-l border-zinc-700"
      testId="audit-log-editor"
    >
      <div className="p-4 border-b border-zinc-700 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-slate-500/20 flex items-center justify-center">
            <FileText size={20} className="text-slate-300" />
          </div>
          <div>
            <h3 className="font-semibold text-white">{label || 'Audit Log'}</h3>
            <p className="text-xs text-zinc-500">Audit Log</p>
          </div>
        </div>
        <button onClick={requestClose} className="p-2 hover:bg-zinc-800 rounded-lg" type="button">
          <X size={18} className="text-zinc-400" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        <div className="rounded-lg border border-zinc-800 bg-zinc-800/40 p-3">
          <p className="text-sm text-zinc-300">
            Record audit events when this workflow step runs.
          </p>
        </div>

        <div>
          <Label className="text-zinc-400 text-sm">Node Name *</Label>
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="mt-2 bg-zinc-800 border-zinc-700 text-white"
            placeholder="Add Audit Logs"
          />
        </div>

        <div>
          <Label className="text-zinc-400 text-sm">Event Name *</Label>
          <Input
            value={eventName}
            onChange={(e) => setEventName(e.target.value)}
            className="mt-2 bg-zinc-800 border-zinc-700 text-white"
            placeholder="workflow.audit"
          />
        </div>

        <div>
          <Label className="text-zinc-400 text-sm">Message / Template</Label>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="mt-2 bg-zinc-800 border-zinc-700 text-white min-h-[120px]"
            placeholder="Audit event recorded for {{agency.name}}"
          />
        </div>

        <div>
          <Label className="text-zinc-400 text-sm">Severity</Label>
          <Select value={severity} onValueChange={setSeverity}>
            <SelectTrigger className="mt-2 bg-zinc-800 border-zinc-700 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-700">
              <SelectItem value="info">Info</SelectItem>
              <SelectItem value="warning">Warning</SelectItem>
              <SelectItem value="error">Error</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center justify-between rounded-lg border border-zinc-800 bg-zinc-800/40 p-3">
          <div>
            <p className="text-sm text-white">Include Context</p>
            <p className="text-xs text-zinc-500">Attach workflow and entity context to the audit entry.</p>
          </div>
          <Switch checked={includeContext} onCheckedChange={setIncludeContext} />
        </div>
      </div>

      <div className="p-4 border-t border-zinc-700 flex justify-end gap-2">
        <Button variant="outline" onClick={requestClose} className="border-zinc-600 text-zinc-300" disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          className="bg-emerald-500 hover:bg-emerald-600 text-white"
          disabled={saving || !label.trim() || !eventName.trim()}
        >
          {saving ? (
            <>
              <Loader2 size={16} className="mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Audit Log'
          )}
        </Button>
      </div>
    </WorkflowStepEditorShell>
  );
};

export default AuditLogEditor;
